// IN-PORTAL PDF VIEWER. Tapping Review / Open showed nothing on the phone
// (Android's Chrome can't display PDFs, so it downloads them) and downloaded
// on the computer too (Chrome's "Download PDFs" setting). The pages are drawn
// right on the portal page instead, with Close and Print. Nothing downloads.
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

// Draw every page of a PDF as a picture (used by the viewer and by Print).
async function renderPages(pdfjs, url, scale) {
  const doc = await pdfjs.getDocument({ url }).promise;
  const pages = [];
  for (let n = 1; n <= doc.numPages; n += 1) {
    const page = await doc.getPage(n);
    const vp = page.getViewport({ scale });
    const c = document.createElement("canvas");
    c.width = Math.floor(vp.width); c.height = Math.floor(vp.height);
    await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
    pages.push(c);
  }
  return pages;
}

// Print the PDF from pictures of its pages — works on the phone and whatever
// the browser's PDF setting is.
export async function printPdfPages(pdfjs, url) {
  const pages = await renderPages(pdfjs, url, 2);
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  document.body.appendChild(frame);
  const d = frame.contentWindow.document;
  d.open();
  d.write("<!doctype html><html><head><style>@page{margin:0}body{margin:0}img{display:block;width:100%;page-break-after:always}img:last-child{page-break-after:auto}</style></head><body>"
    + pages.map((c) => `<img src="${c.toDataURL("image/png")}">`).join("") + "</body></html>");
  d.close();
  await new Promise((r) => setTimeout(r, 400));
  frame.contentWindow.focus();
  frame.contentWindow.print();
  setTimeout(() => frame.remove(), 60000);
}

// Save the PDF to the device (Nov 3). The file is fetched and handed over as a
// download, so it saves even where the browser would otherwise just open it.
export async function downloadPdf(url, title) {
  const base = String(title || decodeURIComponent(String(url).split("?")[0].split("/").pop() || "document"))
    .replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 120) || "document";
  const name = /\.pdf$/i.test(base) ? base : base + ".pdf";
  const res = await fetch(url);
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  const href = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = href; a.download = name; a.style.display = "none";
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(href); a.remove(); }, 60000);
}

export function openPdfViewer(url, opts) {
  const o = opts || {};
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = () => setTimeout(() => { root.unmount(); host.remove(); }, 0);
  root.render(<PdfViewer url={url} title={o.title || "Document"} loadPdfJs={o.loadPdfJs} onClose={close} />);
}

function PdfViewer({ url, title, loadPdfJs, onClose }) {
  const box = useRef(null);
  const [state, setState] = useState("loading");
  const [printing, setPrinting] = useState(false);
  const [saving, setSaving] = useState("");
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const pages = await renderPages(pdfjs, url, Math.min(2.5, (window.devicePixelRatio || 1) * 1.4));
        if (!live || !box.current) return;
        box.current.innerHTML = "";
        pages.forEach((c) => { c.style.cssText = "display:block;width:100%;max-width:900px;margin:0 auto 10px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.3)"; box.current.appendChild(c); });
        setState("ready");
      } catch (e) { if (live) setState("error"); }
    })();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { live = false; window.removeEventListener("keydown", onKey); };
  }, [url]);
  const print = async () => {
    setPrinting(true);
    try { await printPdfPages(await loadPdfJs(), url); } catch (e) { /* stays open */ }
    setPrinting(false);
  };
  const download = async () => {
    setSaving("Saving…");
    try { await downloadPdf(url, title); setSaving(""); } catch (e) { setSaving("Couldn't download — try again"); }
  };
  return (
    <div data-testid="pdf-viewer" style={{ position: "fixed", inset: 0, zIndex: 100000, background: "#525659", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "8px 12px", background: "#0B1628", color: "#fff" }}>
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.9rem" }}>{title}</div>
        <button type="button" data-testid="pdf-viewer-print" disabled={state !== "ready" || printing} onClick={print}
          style={{ background: "#E8650A", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontWeight: 700, minHeight: 44 }}>{printing ? "Preparing…" : "🖨 Print"}</button>
        <button type="button" data-testid="pdf-viewer-download" disabled={state !== "ready" || saving === "Saving…"} onClick={download}
          style={{ background: "#fff", color: "#0B1628", border: "none", borderRadius: 6, padding: "8px 14px", fontWeight: 700, minHeight: 44 }}>{saving || "⬇ Download"}</button>
        <button type="button" data-testid="pdf-viewer-close" onClick={onClose}
          style={{ background: "#fff", color: "#0B1628", border: "none", borderRadius: 6, padding: "8px 14px", fontWeight: 700, minHeight: 44 }}>✕ Close</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 10, WebkitOverflowScrolling: "touch" }}>
        {state === "loading" && <div style={{ color: "#fff", textAlign: "center", marginTop: "2rem" }}>Opening…</div>}
        {state === "error" && <div style={{ color: "#fff", textAlign: "center", marginTop: "2rem" }}>This document couldn't be shown. Close and try again.</div>}
        <div ref={box} />
      </div>
    </div>
  );
}
